import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import * as _ from "lodash";
import Table from "../../../../../../components/core/table";
import Popup from "../../../../../../components/core/popup";
import { useTranslation } from "react-i18next";
import { FieldType } from "../../../../../../constant/enums";
import {
  addProfileForm,
  editProfileForm,
} from "../../../../../../store/profile-form/thunkAction";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../../utils/event";

const FormField = () => {
  const params = useParams();
  const profileId = _.get(params, "id");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const profileFormManage = useSelector((state) => state.profileFormManage);
  const profileForm = _.get(profileFormManage, "profileForm");
  const profileFormId = _.get(profileForm, "_id");
  const isLoadingAddProfileForm = _.get(
    profileForm,
    "isLoadingAddProfileForm",
    false
  );

  const [fields, setFields] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [field, setField] = useState({});
  const [title, setTitle] = useState("");
  const [key, setKey] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [fieldType, setFieldType] = useState(FieldType.TEXT);
  const [options, setOptions] = useState([]);
  const [isFieldDefault, setIsFieldDefault] = useState(false);
  const [isShowName, setIsShowName] = useState(null);
  const [isShowPhone, setIsShowPhone] = useState(true);
  const [isShowEMail, setIsShowEmail] = useState(true);

  useEffect(() => {
    setFields(_.get(profileForm, "fields", []));
  }, [profileForm]);

  useEffect(() => {
    setTitle(_.get(field, "title", ""));
    setKey(_.get(field, "key", ""));
    setIsRequired(_.get(field, "isRequired", false));
    setIsVisible(_.get(field, "isVisible", false));
    setFieldType(_.get(field, "fieldType", FieldType.TEXT));
    setOptions(_.get(field, "options", []));
  }, [field]);

  useEffect(() => {
    const indexName = _.findIndex(
      fields,
      (f) => f.fieldType === FieldType.NAME && f.position !== field.position
    );
    const indexPhone = _.findIndex(
      fields,
      (f) => f.fieldType === FieldType.PHONE && f.position !== field.position
    );
    const indexEmail = _.findIndex(
      fields,
      (f) => f.fieldType === FieldType.EMAIL && f.position !== field.position
    );

    setIsShowName(indexName < 0);
    setIsShowPhone(indexPhone < 0);
    setIsShowEmail(indexEmail < 0);
  }, [fields, field]);

  const closePopup = () => {
    setIsOpen(false);
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOption = {
      ...options[index],
      [field]: value,
    };

    const newOptions = options.map((opt, i) =>
      i === index ? updatedOption : opt
    );

    setOptions(newOptions);
  };

  const addOption = () => {
    setOptions([...options, { key: "", title: "" }]);
  };

  const removeOption = (index) => {
    if (options.length > 0) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    }
  };

  const removeField = async (index) => {
    if (fields.length > 0) {
      const newFields = fields.filter((_, i) => i !== index);

      try {
        await dispatch(
          editProfileForm({
            id: profileFormId,
            data: {
              fields: newFields,
            },
          })
        ).unwrap();

        sendNotifyIziMessageSuccess(t("profile.profile_form_save_success"));
      } catch (error) {
        sendNotifyIziMessageError(t("profile.profile_field_save_error"));
      }
    }
  };

  const updateVisible = async (position, checked) => {
    const newFields = _.cloneDeep(fields);

    const targetField = newFields.find((field) => field.position === position);

    if (targetField) {
      targetField.isVisible = checked;

      try {
        await dispatch(
          editProfileForm({
            id: profileFormId,
            data: {
              fields: newFields,
            },
          })
        ).unwrap();

        sendNotifyIziMessageSuccess(t("profile.profile_form_save_success"));
      } catch (error) {
        sendNotifyIziMessageError(t("profile.profile_field_save_error"));
      }
    }
  };

  const validateField = () => {
    if (!title && !title.trim()) {
      sendNotifyIziMessageError(t("profile.title_is_require"));
      return false;
    }
    if (!key && !key.trim()) {
      sendNotifyIziMessageError(t("profile.key_is_require"));
      return false;
    }
    if (isRequired && !isVisible) {
      sendNotifyIziMessageError(t("profile.visible_is_require"));
      return false;
    }

    if (fieldType !== FieldType.NAME && key === "name") {
      sendNotifyIziMessageError(t("profile.key_is_not_used"));
      return false;
    }

    if (fieldType !== FieldType.PHONE && key === "phone") {
      sendNotifyIziMessageError(t("profile.key_is_not_used"));
      return false;
    }

    if (fieldType !== FieldType.EMAIL && key === "email") {
      sendNotifyIziMessageError(t("profile.key_is_not_used"));
      return false;
    }

    return true;
  };

  const saveField = async () => {
    const isOk = validateField();

    if (!isOk) {
      return;
    }

    const position = _.get(field, "position");

    let newFields;

    const index = _.findIndex(fields, (field) => field.position === position);

    const updatedField = {
      title,
      key,
      fieldType,
      isRequired,
      isVisible,
      options,
      position: index >= 0 ? position : fields.length,
    };

    if (index >= 0) {
      newFields = _.cloneDeep(fields);
      newFields[index] = updatedField;
    } else {
      if (
        _.includes(
          [FieldType.NAME, FieldType.EMAIL, FieldType.PHONE],
          fieldType
        )
      ) {
        const isExistsType = _.findIndex(
          fields,
          (field) => field.fieldType === fieldType
        );

        if (isExistsType >= 0) {
          sendNotifyIziMessageError(t(`profile.${fieldType}_is_exists`));
          return false;
        }
      }
      const isExistsKey = _.findIndex(fields, (field) => field.key === key);

      if (isExistsKey >= 0) {
        sendNotifyIziMessageError(t(`profile.key_is_exists`));
        return;
      }
      newFields = [...fields, updatedField];
    }

    if (profileFormId) {
      try {
        await dispatch(
          editProfileForm({
            id: profileFormId,
            data: {
              fields: newFields,
            },
          })
        ).unwrap();

        sendNotifyIziMessageSuccess(t("profile.profile_form_save_success"));
      } catch (error) {
        sendNotifyIziMessageError(t("profile.profile_field_save_error"));
      }

      setIsOpen(false);
      return;
    }

    const data = {
      profileId,
      preChat: {},
      frame: {},
      fields: newFields,
    };

    if (isLoadingAddProfileForm) {
      return;
    }

    dispatch(addProfileForm(data));

    setIsOpen(false);
  };

  const configTable = {
    showHeader: false,
    header: [
      {
        key: "sort",
        label: "",
        className: "text-center small",
        render: (row) => <img width="20px" src="/img/chat/sort.png" alt="" />,
      },
      {
        key: "sort",
        label: "",
        className: "text-center small",
        render: (row) => {
          const fieldType = _.get(row, "fieldType", FieldType.TEXT);
          let img = "";

          switch (fieldType) {
            case FieldType.TEXT:
              img = "/img/chat/t4.png";
              break;

            case FieldType.EMAIL:
              img = "/img/chat/t1.png";
              break;

            case FieldType.PHONE:
              img = "/img/chat/t2.png";
              break;

            case FieldType.ARENA:
              img = "/img/chat/t5.png";
              break;

            case FieldType.DROPDOWN:
              img = "/img/chat/t3.png";
              break;

            default:
              img = "/img/chat/t4.png";
              break;
          }

          return <img width="20px" src={img} alt="" />;
        },
      },
      {
        key: "title",
        label: "Title",
        className: "text-center small",
      },
      {
        key: "isRequired",
        label: "status",
        className: "text-center small",
        render: (row) => {
          const isRequired = _.get(row, "isRequired", false);

          return isRequired
            ? t("settings.profile.profile_form.required")
            : t("settings.profile.profile_form.optional");
        },
      },
      {
        key: "visible",
        label: "visible",
        className: "text-center small",
        render: (row) => {
          const isVisible = _.get(row, "isVisible", false);
          const position = _.get(row, "position", 0);

          return (
            <label className="form-check form-switch gap-2 d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={isVisible}
                onClick={(e) => updateVisible(position, e.target.checked)}
              />
              <span>{t("settings.profile.profile_form.visible")}</span>
            </label>
          );
        },
      },
      {
        key: "manage",
        label: "manage",
        className: "text-center small",
        render: (row, index) => (
          <div>
            <button
              onClick={() => {
                setField(row);
                setIsOpen(true);
              }}
              className="color-dark"
              style={{ background: "none", border: "none" }}
            >
              <i className="fa fa-pencil-alt"></i>
            </button>
            <button
              className="color-dark"
              style={{ background: "none", border: "none" }}
              onClick={() => {
                removeField(index);
              }}
            >
              <i className="fa fa-trash-alt"></i>
            </button>
          </div>
        ),
      },
    ],
    collection: fields,
  };

  return (
    <div
      className="tab-pane fade active show"
      id="fields-tab-pane"
      role="tabpanel"
      aria-labelledby="fields-tab"
      tabIndex="0"
    >
      <div className="row mb-5">
        <div className="col-lg-3">
          <h5 className="title">
            {t("settings.profile.profile_form.visitor_info")}
          </h5>
          <div className="small color-gray">
            {t("settings.profile.profile_form.visitor_info_desc")}
          </div>
        </div>
        <div className="col-lg-7">
          <div className="row g-4">
            <div className="col-12">
              <div className="">
                {t("settings.profile.profile_form.enable_info_desc")}{" "}
                <span className="text-success">
                  {t("settings.profile.profile_form.temp_desc")}
                </span>
              </div>
            </div>
            <div className="col-12">
              <div className="table-responsive">
                <Table config={configTable} />
              </div>
            </div>
            <div className="col-12">
              <button
                onClick={() => {
                  setIsFieldDefault(false);
                  setField({});
                  setIsOpen(true);
                }}
                className="performs-add-item btn btn-gray btn-outline-gray"
              >
                <i className="fa fa-plus"></i>
                {t("settings.profile.profile_form.add_field")}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card-footer">
        <div className="d-flex gap-3 justify-content-center align-items-center">
          <button
            onClick={() => {
              navigate(`/profile/${profileId}/detail`);
            }}
            className="btn btn-gray btn-outline-gray"
          >
            {t("common.exit")}
          </button>
        </div>
      </div>
      <Popup
        config={{
          isOpen: isOpen,
          onClose: closePopup,
        }}
      >
        <div className="modal-content">
          <div className="modal-body">
            <div className="row g-4">
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.profile_form.field_type")}
                </label>
                <div className="d-flex gap-3 flex-wrap btn-img-box">
                  <button
                    className={
                      fieldType === FieldType.TEXT
                        ? "btn btn-img btn-img-2 active"
                        : "btn btn-img btn-img-2"
                    }
                    onClick={() => {
                      setFieldType(FieldType.TEXT);
                      setKey("");
                      setIsFieldDefault(false);
                    }}
                  >
                    <div className="d-flex gap-2">
                      <img width="20px" src="/img/chat/t4.png" alt="" />
                      <div>{t("settings.profile.profile_form.text_field")}</div>
                    </div>
                    <i className="fa fa-check-circle"></i>
                  </button>
                  {isShowName && (
                    <button
                      className={
                        fieldType === FieldType.NAME
                          ? "btn btn-img btn-img-2 active"
                          : "btn btn-img btn-img-2"
                      }
                      onClick={() => {
                        setFieldType(FieldType.NAME);
                        setKey("name");
                        setIsFieldDefault(true);
                      }}
                    >
                      <div className="d-flex gap-2">
                        <img width="20px" src="/img/chat/t4.png" alt="" />
                        <div>{t("settings.profile.profile_form.name")}</div>
                      </div>
                      <i className="fa fa-check-circle"></i>
                    </button>
                  )}
                  {isShowEMail && (
                    <button
                      className={
                        fieldType === FieldType.EMAIL
                          ? "btn btn-img btn-img-2 active"
                          : "btn btn-img btn-img-2"
                      }
                      onClick={() => {
                        setFieldType(FieldType.EMAIL);
                        setKey("email");
                        setIsFieldDefault(true);
                      }}
                    >
                      <div className="d-flex gap-2">
                        <img width="20px" src="/img/chat/t1.png" alt="" />
                        <div>{t("settings.profile.profile_form.email")}</div>
                      </div>
                      <i className="fa fa-check-circle"></i>
                    </button>
                  )}
                  {isShowPhone && (
                    <button
                      className={
                        fieldType === FieldType.PHONE
                          ? "btn btn-img btn-img-2 active"
                          : "btn btn-img btn-img-2"
                      }
                      onClick={() => {
                        setFieldType(FieldType.PHONE);
                        setKey("phone");
                        setIsFieldDefault(true);
                      }}
                    >
                      <div className="d-flex gap-2">
                        <img width="20px" src="/img/chat/t2.png" alt="" />
                        <div>{t("settings.profile.profile_form.phone")}</div>
                      </div>
                      <i className="fa fa-check-circle"></i>
                    </button>
                  )}
                  <button
                    className={
                      fieldType === FieldType.ARENA
                        ? "btn btn-img btn-img-2 active"
                        : "btn btn-img btn-img-2"
                    }
                    onClick={() => {
                      setFieldType(FieldType.ARENA);
                      setKey("");
                      setIsFieldDefault(false);
                    }}
                  >
                    <div className="d-flex gap-2">
                      <img width="20px" src="/img/chat/t5.png" alt="" />
                      <div>{t("settings.profile.profile_form.text_area")}</div>
                    </div>
                    <i className="fa fa-check-circle"></i>
                  </button>
                  <button
                    className={
                      fieldType === FieldType.DROPDOWN
                        ? "btn btn-img btn-img-2 active"
                        : "btn btn-img btn-img-2"
                    }
                    onClick={() => {
                      setFieldType(FieldType.DROPDOWN);
                      setKey("");
                      setIsFieldDefault(false);
                    }}
                  >
                    <div className="d-flex gap-2">
                      <img width="20px" src="/img/chat/t3.png" alt="" />
                      <div>{t("settings.profile.profile_form.dropdown")}</div>
                    </div>
                    <i className="fa fa-check-circle"></i>
                  </button>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.profile_form.title_field")}
                </label>
                <input
                  className="form-control"
                  placeholder={t("settings.profile.profile_form.title_field")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.profile_form.key")}
                </label>
                <input
                  className="form-control"
                  placeholder={t("settings.profile.profile_form.key")}
                  value={key}
                  style={isFieldDefault ? { backgroundColor: "#dfdfdf" } : {}}
                  readOnly={isFieldDefault}
                  onChange={(e) => setKey(e.target.value)}
                />
              </div>
              <div className="col-12">
                <div className="d-flex gap-5">
                  <label className="form-check form-switch gap-2 d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={isVisible}
                      onChange={(e) => setIsVisible(e.target.checked)}
                    />
                    <span>{t("settings.profile.profile_form.visible")}</span>
                  </label>
                  <label className="form-check form-switch gap-2 d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={isRequired}
                      onChange={(e) => setIsRequired(e.target.checked)}
                    />
                    <span>{t("settings.profile.profile_form.required")}</span>
                  </label>
                </div>
              </div>
              {fieldType === FieldType.DROPDOWN && (
                <div className="col-12">
                  <div className="p-3 rounded-2 bg-dark bg-opacity-10">
                    <label className="form-label text-uppercase">
                      {t("settings.profile.profile_form.optional")}
                    </label>

                    {options.map((option, index) => (
                      <div key={index} className="row align-items-center mb-2">
                        <div className="col-5">
                          <input
                            className="form-control"
                            placeholder={t("settings.profile.profile_form.key")}
                            value={option.key}
                            onChange={(e) =>
                              handleOptionChange(index, "key", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-5">
                          <input
                            className="form-control"
                            placeholder={t(
                              "settings.profile.profile_form.title_field"
                            )}
                            value={option.title}
                            onChange={(e) =>
                              handleOptionChange(index, "title", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-2 text-end">
                          <button
                            className="btn btn-link text-danger"
                            onClick={() => removeOption(index)}
                          >
                            <i className="fa fa-times-circle"></i>
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="row align-items-center">
                      <div className="col-5">
                        <button
                          type="button"
                          className="w-100 btn btn-gray btn-outline-gray bg-white mt-3 text-start"
                          onClick={addOption}
                        >
                          {t("settings.profile.profile_form.add_option")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer" style={{ gap: "5px" }}>
            <button
              onClick={() => setIsOpen(false)}
              type="button"
              className="btn btn-gray btn-outline-gray"
            >
              {t("common.closed")}
            </button>
            <button
              onClick={() => {
                saveField();
              }}
              type="button"
              className="btn btn-primary"
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default FormField;
